import { CalendarIcon, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@shared/components/ui/button";
import { cn } from "@shared/utils/cn";
import { verifyAttrs } from "@/shared/verify/core/contract";
import { useEffect, useRef, useState } from "react";
import type { AvailabilityDisplayType } from "../types/calendar";
import { isDisplayTypeClickable } from "../utils/availability";

/**
 * Format a Date as a stable YYYY-MM-DD string for verify attributes and tests.
 * Local-time based to match how the calendar groups by calendar day.
 */
function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface DateOption {
  day: string;
  date: number;
  month: string;
  fullDate: Date;
  /** Display type for styling (from merged calendar) */
  displayType?: AvailabilityDisplayType;
  /** Whether the date has any availability */
  hasAvailability?: boolean;
  /** Whether this is a special day (event, holiday, etc.) */
  isSpecialDay?: boolean;
  /** Whether to show the special day indicator */
  highlightSpecialDay?: boolean;
}

interface DateSelectorProps {
  dates: DateOption[];
  selectedDateIndex: number | null;
  onSelect: (index: number) => void;
  onShowCalendar: () => void;
  /** Hide the full-calendar shortcut button at the end of the carousel */
  hideCalendarShortcut?: boolean;
  /** Show loading skeleton while calendar data is loading */
  isLoading?: boolean;
}

/**
 * Get button styles based on availability display type
 */
function getDateButtonStyles(
  isSelected: boolean,
  displayType?: AvailabilityDisplayType
): string {
  // If selected, always use brand color
  if (isSelected) {
    return "bg-brand text-brand-foreground shadow-md";
  }

  // If no availability data yet, use default style
  if (displayType === undefined) {
    return "bg-card border border-border translucent:border-surface-border text-foreground/70 hover:border-brand-light";
  }

  // Style based on display type.
  // 'available' / 'request' use the neutral default — this shortcut strip
  // doesn't reflect real per-slot availability (the full calendar does), so
  // marking every open day green is misleading. Yellow/blue stay for genuine
  // alternative-path states (waiting list, walk-in).
  switch (displayType) {
    case 'waiting-list':
      return "bg-card border border-yellow-200 text-foreground/70 hover:border-yellow-400 hover:bg-yellow-500/10";
    case 'walk-in':
      return "bg-card border border-blue-200 text-foreground/70 hover:border-blue-400 hover:bg-blue-500/10";
    case 'not-available':
      return "bg-foreground/5 border border-border translucent:border-surface-border text-foreground/40 cursor-not-allowed";
    case 'closed':
      return "bg-foreground/10 border border-border translucent:border-surface-border text-foreground/30 cursor-not-allowed";
    default:
      return "bg-card border border-border translucent:border-surface-border text-foreground/70 hover:border-brand-light";
  }
}


const DateSelector = ({ dates, selectedDateIndex, onSelect, onShowCalendar, hideCalendarShortcut = false, isLoading }: DateSelectorProps) => {
  const { t } = useTranslation('booking');
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Aggregate state for the unit-level data-verify-* contract. These fields
  // describe what an agent could read to make decisions; they intentionally
  // exclude UI-only state like hover/scroll arrows.
  const dateCount = dates.length;
  const hasSelection = selectedDateIndex !== null;
  const selectedIso =
    selectedDateIndex !== null && dates[selectedDateIndex]
      ? toIsoDate(dates[selectedDateIndex].fullDate)
      : null;
  const clickableCount = dates.filter((d) => isDisplayTypeClickable(d.displayType)).length;
  const disabledCount = dateCount - clickableCount;

  const checkScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    setShowLeftArrow(container.scrollLeft > 0);
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      // Initial check
      checkScroll();
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  const scrollLeft = () => {
    const container = containerRef.current;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const container = containerRef.current;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <div
      className="w-full"
      {...verifyAttrs("DateSelector", {
        dateCount,
        clickableCount,
        disabledCount,
        hasSelection,
        selectedIndex: selectedDateIndex,
        selectedDate: selectedIso,
        isLoading: Boolean(isLoading),
        hideCalendarShortcut,
      })}
    >
      <div className="relative">
        <div
          ref={containerRef}
          className="w-full overflow-x-auto pb-6 scrollbar-none px-6 dates-container [mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)]"
        >
          <div className="flex space-x-3 min-w-max">
            {dates.map((day, index) => {
              const isSelected = selectedDateIndex === index;
              const isClickable = isDisplayTypeClickable(day.displayType);
              const buttonStyles = getDateButtonStyles(isSelected, day.displayType);

              return (
                <button
                  key={index}
                  data-testid="date-card"
                  className={cn(
                    "flex flex-col items-center p-4 min-w-[90px] rounded-xl transition duration-200 active:scale-[0.96] shadow-sm relative",
                    buttonStyles
                  )}
                  onClick={() => isClickable && onSelect(index)}
                  disabled={!isClickable}
                  aria-disabled={!isClickable}
                  {...verifyAttrs("DateSelectorDay", {
                    index,
                    date: toIsoDate(day.fullDate),
                    displayType: day.displayType ?? "unknown",
                    selected: isSelected,
                    disabled: !isClickable,
                    clickable: isClickable,
                    isSpecialDay: Boolean(day.isSpecialDay),
                  })}
                >
                  {/* Special day star indicator */}
                  {day.isSpecialDay && day.highlightSpecialDay && !isSelected && (
                    <Star
                      size={12}
                      className="absolute top-1.5 right-1.5 text-yellow-500 fill-yellow-400"
                    />
                  )}
                  <span className={cn(
                    "text-sm font-medium",
                    isSelected ? "opacity-90" : ""
                  )}>
                    {day.day}
                  </span>
                  <span className={cn(
                    "text-2xl font-medium my-1",
                    isSelected ? "text-brand-foreground" : !isClickable ? "text-foreground/40" : "text-foreground"
                  )}>
                    {day.date}
                  </span>
                  <span className={cn(
                    "text-xs",
                    isSelected ? "opacity-80" : !isClickable ? "text-foreground/40" : "text-foreground/50"
                  )}>
                    {day.month}
                  </span>
                </button>
              );
            })}
            {!hideCalendarShortcut && (
              <button
                className="flex flex-col items-center justify-center p-4 min-w-[90px] rounded-xl bg-brand text-brand-foreground shadow-md hover:bg-brand-dark active:scale-[0.96] transition duration-200"
                onClick={onShowCalendar}
              >
                <CalendarIcon size={24} className="mb-1" />
                <span className="text-xs font-medium">{t('CALENDAR_BUTTON')}</span>
              </button>
            )}
          </div>
        </div>

        {showLeftArrow && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-full flex items-center justify-start pl-2">
            <Button
              size="icon"
              variant="ghost"
              className="rounded-full shadow-sm border border-border translucent:border-surface-border bg-card hover:bg-foreground/5"
              onClick={scrollLeft}
              aria-label={t('A11Y_SCROLL_DATES_LEFT')}
            >
              <ChevronLeft size={18} className="text-foreground/70" />
            </Button>
          </div>
        )}

        {showRightArrow && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 h-full flex items-center justify-end pr-2">
            <Button
              size="icon"
              variant="ghost"
              className="rounded-full shadow-sm border border-border translucent:border-surface-border bg-card hover:bg-foreground/5"
              onClick={scrollRight}
              aria-label={t('A11Y_SCROLL_DATES_RIGHT')}
            >
              <ChevronRight size={18} className="text-foreground/70" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateSelector;
